import { useMemo, useRef } from 'react';
import { classnames } from 'tailwindcss-classnames';
import RingLoader from 'react-spinners/RingLoader';
import { BeConnected } from '.generated/templates/Project.BeConnected.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import Button from 'components/helpers/Button';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import Container from 'components/helpers/Container';
import Iframe from 'components/helpers/Iframe';
import Heading, { HeadingAlign, HeadingSizes, propsFromFields } from 'components/helpers/Heading';
import { useRefreshEmbedOnTimeout, useLookerMessageListener } from 'lib/looker/client-hooks';
import { useRouter } from 'next/router';
import fnNoOp from 'lib/util/no-op';
import { ValidHeadingLevels } from 'lib/sitecore/component-params';

export type LookerEmbedProps = Sitecore.Override.ComponentBase &
  BeConnected.LookerEmbed.Fields.LookerEmbed;

const makeHeadingProps = (
  headingLevel?: Sitecore.Override.ItemEx,
  headingAlignment?: Sitecore.Override.ItemEx,
  headingText?: Field<string>,
  description?: Field<string>,
  defaults?: {
    headingLevel?: ValidHeadingLevels;
    headingAlignment?: HeadingAlign;
    headingSize?: HeadingSizes;
    headingText?: string;
    description?: string;
  }
) => {
  const ret = propsFromFields({
    headingLevel,
    headingAlignment,
    headingText: headingText ?? ({} as Field<string>),
    description: description ?? ({} as Field<string>),
  });
  if (defaults) {
    ret.headingLevel = ret.headingLevel ?? defaults.headingLevel;
    ret.headingAlignment = ret.headingAlignment ?? defaults.headingAlignment;
    ret.headingSize = ret.headingSize ?? defaults.headingSize;
    if (!ret.headingText?.value) {
      ret.headingText = {
        ...(ret.headingText ?? {}),
        value: defaults.headingText ?? '',
      };
    }
    if (!ret.description?.value) {
      ret.description = {
        ...(ret.description ?? {}),
        value: defaults.description ?? '',
      };
    }
  }
  return ret;
};

/**
 * Private component supporting setting field values in experience editor.
 */
const LookerEmbedExperienceEditorSupport = ({ fields }: LookerEmbedProps) => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const { embedId } = fields;
  return (
    <>
      <p>Click here to edit Looker Embed in Experience Editor</p>
      <div>
        <h3>Looker Embed Id</h3>
        <Text field={embedId} />
      </div>
      <div>
        <h3>Loading Message</h3>
        <Heading
          {...makeHeadingProps(
            fields.loadingMessageHeadingLevel,
            fields.loadingMessageHeadingAlignment,
            fields.loadingMessageHeadingText,
            fields.loadingMessageDescription
          )}
        />
      </div>
      <div>
        <h3>Error Message</h3>
        <Heading
          {...makeHeadingProps(
            fields.errorMessageHeadingLevel,
            fields.errorMessageHeadingAlignment,
            fields.errorMessageHeadingText,
            fields.errorMessageDescription
          )}
        />
      </div>
    </>
  );
};

const LookerEmbed = ({ fields, rendering, ...props }: LookerEmbedProps): JSX.Element => {
  const isEE = useExperienceEditor();
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const { embedId, disableLoadingScreen: disableLoadingScreenField } = fields ?? {};
  const disableLoadingScreen = disableLoadingScreenField?.value === true;
  const { uid: myComponentUid } = rendering;
  const embedIdValue = embedId?.value;
  const theIframeId = `looker-embed-${myComponentUid}`;

  const { embedUrl, isIFrameLoaded, setIFrameLoaded, hadUnexpectedError } =
    useRefreshEmbedOnTimeout({
      isEE,
      embedIdValue,
      disableLoadingScreen,
    });

  useLookerMessageListener({
    setIFrameLoaded,
    theIframeId,
    isEE,
    disableLoadingScreen,
  });

  const theIFrame = useMemo(() => {
    const primaryIframeClasses = classnames('w-full', 'pb-2', {
      hidden: !isIFrameLoaded && disableLoadingScreen !== true,
    });
    return (
      <Iframe
        src={embedUrl}
        iFrameRef={iFrameRef}
        className={primaryIframeClasses}
        height="400px"
        width="100%"
        title="Looker Embed"
        loading="eager"
        id={theIframeId}
      />
    );
  }, [embedUrl, iFrameRef, isIFrameLoaded, theIframeId, disableLoadingScreen]);
  const { reload: reloadPage } = useRouter() ?? { reload: fnNoOp };
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  // Helper for whether or not we have an embed url
  const hasEmbedUrl = (embedUrl ?? '').length > 0;
  // IFrame should be hidden if the iframe is not loaded

  return (
    <Container size="standard" dataComponent="authorable/beconnected/lookerembed">
      {isEE && (
        <LookerEmbedExperienceEditorSupport fields={fields} rendering={rendering} {...props} />
      )}
      {!isEE && (
        <>
          {hadUnexpectedError && (
            <>
              <div
                className={classnames(
                  'absolute',
                  'left-1/2',
                  'top-1/3',
                  'z-[9999]',
                  '-translate-x-1/2',
                  '-translate-y-1/2'
                )}
              >
                <div
                  className={classnames(
                    'grid',
                    'grid-flow-col',
                    'grid-rows-2',
                    'justify-items-center',
                    'rounded-lg',
                    'border-0',
                    'bg-white',
                    'p-7',
                    'shadow-lg',
                    'outline-none',
                    'focus:outline-none'
                  )}
                >
                  <div>
                    <Heading
                      {...makeHeadingProps(
                        fields.errorMessageHeadingLevel,
                        fields.errorMessageHeadingAlignment,
                        fields.errorMessageHeadingText,
                        fields.errorMessageDescription,
                        {
                          headingAlignment: 'center',
                          headingLevel: 'h2',
                          headingSize: 'l',
                          headingText: "We're Sorry",
                          description: 'An unexpected error has occurred.  Please try again later.',
                        }
                      )}
                    />
                  </div>
                  <div className={classnames('py-2')}>
                    <Button onClick={reloadPage}>Close</Button>
                  </div>
                </div>
              </div>
            </>
          )}
          {!hadUnexpectedError && (
            <>
              {!isIFrameLoaded && (
                <span
                  className={classnames(
                    'flex',
                    'flex-col',
                    'items-center',
                    'justify-center',
                    'pb-2'
                  )}
                >
                  <Heading
                    {...makeHeadingProps(
                      fields.loadingMessageHeadingLevel,
                      fields.loadingMessageHeadingAlignment,
                      fields.loadingMessageHeadingText,
                      fields.loadingMessageDescription
                    )}
                  />
                  <RingLoader color="#00355E" loading aria-label="Please wait - Metrics loading" />
                </span>
              )}
              {hasEmbedUrl && theIFrame}
            </>
          )}
        </>
      )}
    </Container>
  );
};
export default LookerEmbed;
